import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Timestamp } from 'firebase/firestore';
import DOMPurify from 'dompurify';
import LazyImage from '../common/LazyImage';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';

interface Article {
    id: string;
    title: string;
    titleEn?: string;
    content: string;
    contentEn: string;
    category: string;
    status: string;
    publishDate: Timestamp;
    cover?: string;
    showOnHome?: boolean;
}

const ArticleDetail: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [article, setArticle] = useState<Article | null>(null);
    const [loading, setLoading] = useState(true);

    // 判断文章类型，返回对应的列表页面
    const getReturnPath = (category: string) => {
        // 项目类型的分类
        const programCategories = ['saturday', 'afterSchool', 'summerCamp', 'satAct'];
        return programCategories.includes(category) ? '/programs' : '/news';
    };

    useEffect(() => {
        const fetchArticle = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, 'articles', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setArticle({
                        id: docSnap.id,
                        ...data,
                        publishDate: data.publishDate as Timestamp
                    } as Article);
                }
            } catch (error) {
                console.error('获取文章详情失败:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchArticle();
    }, [id]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <p className="text-gray-500 mb-4">{t('article.notFound')}</p>
                <Link to="/news" className="text-blue-500 hover:text-blue-600">
                    {t('article.backToList')}
                </Link>
            </div>
        );
    }

    const returnPath = getReturnPath(article.category);

    // 修改 HTML 清理和处理函数
    const createCleanHtml = (content: string) => {
        // 创建一个临时的 div 来解析 HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(content, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['style', 'class', 'align', 'src', 'width', 'height']
        });

        // 替换所有 img 标签为自定义组件的占位符
        const imgs = tempDiv.getElementsByTagName('img');
        Array.from(imgs).forEach((img, index) => {
            const src = img.getAttribute('src') || '';
            const alt = img.getAttribute('alt') || '';
            const className = img.getAttribute('class') || '';
            
            // 创建一个占位符标签，稍后会被替换为 LazyImage 组件
            const placeholder = document.createElement('img');
            placeholder.setAttribute('data-lazy-src', src);
            placeholder.setAttribute('data-lazy-alt', alt);
            placeholder.setAttribute('data-lazy-class', className);
            placeholder.setAttribute('data-lazy-index', index.toString());
            img.parentNode?.replaceChild(placeholder, img);
        });

        return tempDiv.innerHTML;
    };

    // 渲染处理后的内容
    const renderContent = () => {
        if (!article) return null;

        const cleanHtml = createCleanHtml(i18n.language === 'zh' ? article.content : article.contentEn);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cleanHtml;

        // 查找所有需要替换为 LazyImage 的占位符
        const placeholders = tempDiv.querySelectorAll('img[data-lazy-src]');
        placeholders.forEach(placeholder => {
            const src = placeholder.getAttribute('data-lazy-src') || '';
            const alt = placeholder.getAttribute('data-lazy-alt') || '';
            const className = placeholder.getAttribute('data-lazy-class') || '';
            
            // 创建 LazyImage 组件的包装元素
            const wrapper = document.createElement('div');
            wrapper.className = 'my-4';
            wrapper.innerHTML = `<div class="lazy-image" data-src="${src}" data-alt="${alt}" data-class="${className}"></div>`;
            placeholder.parentNode?.replaceChild(wrapper, placeholder);
        });

        return (
            <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: tempDiv.innerHTML }}
                ref={el => {
                    if (el) {
                        // 找到所有需要替换的占位符
                        const lazyImages = el.getElementsByClassName('lazy-image');
                        Array.from(lazyImages).forEach(placeholder => {
                            const src = placeholder.getAttribute('data-src') || '';
                            const alt = placeholder.getAttribute('data-alt') || '';
                            const className = placeholder.getAttribute('data-class') || '';

                            // 创建 LazyImage 组件的包装元素
                            const lazyImageContainer = document.createElement('div');
                            const root = createRoot(lazyImageContainer);
                            root.render(<LazyImage src={src} alt={alt} className={className} />);
                            placeholder.parentNode?.replaceChild(lazyImageContainer, placeholder);
                        });
                    }
                }}
            />
        );
    };

    return (
        <div className="min-h-screen bg-white pt-24 mt-12 sm:pt-32">
            <div className="mx-auto max-w-3xl px-6 lg:px-8">
                <div className="mb-8">
                    <Link
                        to={returnPath}
                        className="text-blue-500 hover:text-blue-600 flex items-center space-x-1"
                    >
                        <span>←</span>
                        <span>{t('article.backToList')}</span>
                    </Link>
                </div>
                
                <article className="prose prose-lg max-w-none">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        {article.title}
                    </h1>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-8">
                        <time dateTime={article.publishDate.toDate().toISOString()}>
                            {article.publishDate.toDate().toLocaleDateString('zh-CN')}
                        </time>
                        <span>•</span>
                        <span>{article.category}</span>
                    </div>

                    {renderContent()}
                </article>

                <div className="mt-12 border-t pt-6">
                    <Link
                        to={returnPath}
                        className="text-blue-500 hover:text-blue-600"
                    >
                        ← {t('article.backToList')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ArticleDetail; 